/**
 * 생성기 한 번 실행의 입력과 결과 — 실행(`aiGenerateDockRunner`)과 화면이 같은 모양을 본다.
 */
import { type AiGenerateDockSourceType } from '../consts/aiGenerateDock.const';

import {
  type AiGenerateDockErrorOutcome,
  type AiGenerateDockUsage,
} from './aiGenerateDockOutcome';
import {
  type AiGenerateDockSubmission,
  buildDirectGenerateRequest,
} from './aiGenerateDockRequest';
import {
  type AiBuilderDifficultyHint,
  type AiBuilderQuizTypeHint,
} from './types';

export type AiGenerateDockSource =
  | { kind: 'none' }
  | { kind: 'file'; file: Blob; fileName: string }
  | { kind: 'link'; url: string };

/** 이 실행이 이미 서버 세션에 등록한 자료 — 같은 세션이면 다시 시도할 때 다시 올리지 않는다. */
export type AiGenerateDockRegisteredSource = {
  sourceRef: string;
  sessionId: string;
};

export type AiGenerateDockRunInput = {
  sourceType: AiGenerateDockSourceType;
  /** 주제 탭은 주제, 자료 탭은 다룰 범위(선택). */
  topic: string;
  source: AiGenerateDockSource;
  /** 앞선 실행에서 등록을 마친 자료. 실행이 채우고 다시 시도가 넘겨받는다. */
  registeredSource?: AiGenerateDockRegisteredSource;
  /**
   * 첨부할 때 시작한 사전 등록 — 제출 시점에 끝나지 않았으면 실행이 기다렸다 쓴다.
   * 실패하면 null 로 끝나고, 실행이 원본으로 다시 등록한다.
   */
  pendingRegistration?: Promise<AiGenerateDockRegisteredSource | null>;
  quizTypes: AiBuilderQuizTypeHint[];
  /** 남은 칸으로 이미 줄인 문항 수. */
  count: number;
  difficulty: AiBuilderDifficultyHint;
};

export type AiGenerateDockRunRejection = Exclude<
  AiGenerateDockErrorOutcome,
  { kind: 'failed' }
>;

export type AiGenerateDockRunResult =
  | { kind: 'success'; created: number; usage: AiGenerateDockUsage | null }
  | {
      kind: 'partial';
      created: number;
      failed: number;
      usage: AiGenerateDockUsage | null;
    }
  | { kind: 'failed' }
  /** 생성까지 가지 못하고 끝났다(자료 확인 실패 등). 입력을 그대로 둔다. */
  | { kind: 'not_generated' }
  | { kind: 'rejected'; rejection: AiGenerateDockRunRejection }
  | { kind: 'aborted' };

export const otherRejection = (
  code: string | null,
): AiGenerateDockRunResult => ({
  kind: 'rejected',
  rejection: { kind: 'other', code },
});

export const toRejectedResult = (
  outcome: AiGenerateDockErrorOutcome,
): AiGenerateDockRunResult =>
  outcome.kind === 'failed'
    ? { kind: 'failed' }
    : { kind: 'rejected', rejection: outcome };

const invalidRequest = (fields: string[]): AiGenerateDockRunResult => ({
  kind: 'rejected',
  rejection: { kind: 'invalid_request', field: fields[0] ?? null },
});

export const toDockSubmission = (
  input: AiGenerateDockRunInput,
  sourceRef: string,
): AiGenerateDockSubmission => {
  const conditions = {
    quizTypes: input.quizTypes,
    count: input.count,
    difficulty: input.difficulty,
  };
  if (input.sourceType === 'query') {
    return { ...conditions, sourceType: 'query', topic: input.topic };
  }
  return {
    ...conditions,
    sourceType: input.sourceType,
    sourceRef,
    scope: input.topic,
  };
};

/**
 * 네트워크 전에 입력을 검사한다 — 자료 등록·세션 생성 비용을 쓰지 않게.
 * 자료 등록 전이라 source_ref 자리는 임시 값으로 채우고, 자료 유무는 따로 본다.
 */
export const validateAiGenerateDockRunInput = (
  input: AiGenerateDockRunInput,
): AiGenerateDockRunResult | null => {
  if (input.sourceType !== 'query' && input.source.kind === 'none') {
    return invalidRequest(['source_refs']);
  }
  const request = buildDirectGenerateRequest(
    toDockSubmission(input, 'pending'),
  );
  return request.ok ? null : invalidRequest(request.fields);
};
